import 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import { AgGridReact } from 'ag-grid-react';
import React, { ComponentProps, FC } from 'react';
import { setDisplayName } from 'recompose';
import styled, { css } from 'styled-components';
import { ClassNameOnlyProps } from 'components/ozone-component';
import { BodyShort01 } from 'components/styles';
import { theme } from 'components/styles/theme';

interface Props {
  size?: keyof typeof theme.table.row_height;
  autoSetHeaderHeight?: boolean;
}

type IntermediaryProps = Props & ComponentProps<typeof AgGridReact>;

const Intermediary: FC<IntermediaryProps> = ({ size, ...rest }) => (
  <AgGridReact {...rest} />
);

export const Grid: FC<IntermediaryProps & ClassNameOnlyProps> = styled(
  setDisplayName('WithoutSize(AgGridReact)')(Intermediary),
).attrs(
  ({
    size = 'medium',
    theme: {
      pixels,
      table: { row_height },
    },
  }) => ({
    headerHeight: 32,
    rowHeight: pixels(row_height[size]),
  }),
)`
  font-family: var(--font-family);

  --ag-row-hover-color: var(--table-row-hover);
  --ag-background-color: var(--white-100);
  --ag-odd-row-background-color: var(--white-100);
  --ag-data-color: var(--gray-80);
  --grid-header-color: var(--gray-90);
  --ag-foreground-color: var(--gray-100);
  --ag-row-border-color: var(--table-border-color);
  --cell-padding: var(--spacing-04);
  --ag-range-selection-border-color: transparent;
  --ag-value-change-value-highlight-background-color: var(--blue-30);
  --grid-background: var(--white-100);
  --header-background: var(--coolest-gray-20);
  --ag-selected-row-background-color: var(--blue-20);
  --cell-focus-color: var(--blue-10);

  .ag-theme-balham {
    font-family: var(--font-family);

    &.ag-popup .ag-popup-editor {
      background: var(--white-100);
    }
  }

  .ag-popup {
    .ag-popup-child:not(.ag-tooltip-custom) {
      box-shadow: var(--shadow-32);
    }

    .ag-popup-child:not(.ag-tooltip-custom):not(.ag-menu) {
      border: none;
    }

    .ag-popup-child {
      z-index: var(--zindex-layer-01);
    }

    .ag-popup-editor {
      min-width: 200px;
      display: flex;
      align-items: center;
      z-index: var(--zindex-layer-03);
    }
  }

  .ag-cell-data-changed-animation {
    background: transparent !important;
  }

  .ag-selection-checkbox {
    opacity: 0;
    transition: opacity var(--animation-duration-medium) ease-in-out;

    input {
      cursor: pointer;
    }
  }

  .ag-row-hover .ag-selection-checkbox {
    opacity: 1;
  }

  ${({
    theme: {
      table: {
        row_height: { small, medium, large },
      },
    },
  }) => css`
    --cell-height-small: ${small};
    --cell-height-default: ${medium};
    --cell-height-medium: ${medium};
    --cell-height-large: ${large};
  `};

  .ag-root-wrapper,
  .ag-root {
    background: var(--grid-background);
  }

  .ag-root-wrapper,
  .ag-header {
    border: none;
  }

  .ag-header,
  .ag-status-bar {
    background: var(--header-background);
    box-shadow: none;
    position: relative;
    z-index: var(--zindex-modal-lowest);
    border-top: none;

    ${({ autoSetHeaderHeight = false }) =>
      autoSetHeaderHeight === false &&
      css`
        height: var(--cell-height-default) !important;
        min-height: var(--cell-height-default) !important;
      `};

    ${({ autoSetHeaderHeight = false, size = 'medium' }) =>
      autoSetHeaderHeight === false &&
      css`
        &,
        .ag-status-bar {
          height: var(--cell-height-${size}) !important;
          min-height: var(--cell-height-${size}) !important;
        }
      `};
  }

  .ag-header {
    ${({ autoSetHeaderHeight = false }) =>
      autoSetHeaderHeight === false &&
      css`
        height: var(--cell-height-small) !important;
        min-height: var(--cell-height-small) !important;
      `};
  }

  .ag-row-focus.ag-row-selected,
  .ag-row-selected .ag-cell-focus {
    background: var(--ag-selected-row-background-color);
  }

  .ag-pinned-left-cols-container .ag-row,
  .ag-pinned-left-header,
  .ag-cell.ag-cell-last-left-pinned:not(.ag-cell-range-right):not(.ag-cell-range-single-cell) {
    border-right: -1px solid var(--table-border-color);
  }

  .grid-cell-editable {
    cursor: pointer;

    .ant-tag,
    .ozone-select__multi-value__remove,
    .ozone-select__clear-indicator,
    .ozone-select__option {
      cursor: pointer;
    }
  }
  .grid-cell-not-editable {
    cursor: not-allowed;
  }
  .ag-pinned-right-header {
    border-left: 1px solid var(--border-color);
  }

  .ag-header-cell,
  .ag-cell,
  .ag-row {
    ${BodyShort01};
    display: flex;
    align-items: center;
    overflow: hidden;
  }

  .ag-header-cell-label .ag-header-custom-icon {
    margin-right: var(--spacing-03);
    display: flex;
  }

  .ag-header-cell-label .ag-header-custom-icon + .ag-header-custom-icon {
    transform: scale(0.75);
    margin-left: calc(-1 * var(--spacing-02));
  }

  .ag-header-cell {
    padding: 0 var(--cell-padding);
  }

  .ag-cell {
    padding: 0 calc(var(--cell-padding) - 1px);
  }

  .ag-row-group {
    align-items: center;
  }

  .ag-ltr .ag-group-expanded,
  .ag-ltr .ag-group-contracted {
    margin-right: var(--layout-01);
  }

  .ag-row-group-leaf-indent.ag-row-group-indent-0 {
    padding-left: var(--layout-03);
  }

  .ag-ltr [class*='ag-row-level-'] .ag-row-group-leaf-indent {
    margin-left: var(--layout-03);
  }

  .ag-ltr .ag-row-level-0 .ag-row-group-leaf-indent {
    margin-left: initial;
  }

  .ag-header-cell-label {
    font-weight: 600;
    color: var(--grid-header-color);
  }

  .ag-row,
  .ag-header-row {
    height: 32px;
    border-color: transparent;
  }

  /** TODO: Remove td patchwork @artfuldev */
  .ag-react-container {
    flex-grow: 1;
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .private {
    background: var(--private-01);
  }

  .ag-cell-focus {
    background: var(----cell-focus-color);
    border: 1px solid var(--blue-30) !important;
    --ag-border-color: var(--blue-30);
  }

  .spacer {
    display: flex;
    align-items: center;
  }

  .spacer > * {
    margin-right: var(--spacing-02) !important;
  }

  .spacer.spacer-medium > * {
    margin-right: calc(var(--spacing-03) + var(--spacing-02)) !important;
  }

  .spacer > *:last-child {
    margin-right: 0 !important;
  }

  .extra {
    background: var(--cool-gray-30);
  }

  .ant-avatar-string {
    transform: scale(0.857143) translateX(-50%);
    color: white;
    opacity: 1 !important;
  }

  .ag-row-focus {
    background: var(--ag-row-hover-color);
  }

  .ozone-select__single-value .ant-tag {
    cursor: default;
  }

  .ozone-select__dropdown-indicator,
  .ozone-select__indicator-separator {
    display: none;
  }

  .ag-overlay-no-rows-wrapper {
    display: none;
  }

  .assignee > .ag-react-container {
    justify-content: center;
  }

  .highlighted {
    background-color: var(--ag-selected-row-background-color);
  }
`;
